import { useState, type ReactElement } from "react";
import { CheckCircle, ChevronsRight, Loader2, PauseCircle, PlayCircle } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useNextCookingSessionStage, useUpdateCookingSession } from "@/hooks/cooking-sessions/mutations";
import { useRecipeStages } from "@/hooks/recipes";
import { getApiErrorMessage } from "@/lib/api/api";
import { canMoveToNextStage } from "@/lib/multi-view";
import type { CookingSession } from "@/types/cooking-session";
import { deselectSession } from "./selection-store";

type Action = "start" | "pause" | "resume" | "next" | "done";

interface SessionActionsProps {
  session: CookingSession;
  /** Reports a failed action; the screen shows it once, not per card. */
  onError: (message: string) => void;
  className?: string;
}

/**
 * Start / Pause / Next stage / Mark done for one recipe (YC-24 to YC-26).
 *
 * Product's call for the first release: anyone who can see the screen can
 * pause, move to the next stage or mark done, so those carry no permission
 * check of their own (the API still enforces its own). Start keeps the check
 * the deck has always had, `cooking-session:cook`. Starting hands the recipe to
 * the starter when it has no chef, which the server does (YC-11).
 */
export function SessionActions({ session, onError, className }: SessionActionsProps) {
  const { t } = useLingui();
  const { hasPermission } = useAuth();
  const update = useUpdateCookingSession();
  const nextStage = useNextCookingSessionStage();
  const stages = useRecipeStages(session.recipeId);
  const [pending, setPending] = useState<Action | null>(null);

  const recipeStageCount = stages.data?.filter((stage) => stage.kind === "STAGE").length;
  const showNext = canMoveToNextStage(session, recipeStageCount);
  const busy = pending !== null;

  async function run(action: Action, call: () => Promise<unknown>) {
    setPending(action);
    try {
      await call();
      if (action === "done") deselectSession(session.id);
    } catch (error) {
      onError(getApiErrorMessage(error, t`That didn't go through. Try again.`));
    } finally {
      setPending(null);
    }
  }

  const setStatus = (status: "COOKING" | "PAUSED" | "COMPLETED") =>
    update.mutateAsync({ id: session.id, data: { status } });

  const icon = (action: Action, idle: ReactElement) =>
    pending === action ? <Loader2 className="animate-spin" /> : idle;

  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      {session.status === "ON_DECK" && hasPermission("cooking-session:cook") && (
        <Button type="button" variant="blue" disabled={busy} onClick={() => run("start", () => setStatus("COOKING"))} className="gap-2">
          {icon("start", <PlayCircle />)}
          {t`Start`}
        </Button>
      )}
      {session.status === "COOKING" && (
        <Button type="button" variant="brand-outline" disabled={busy} onClick={() => run("pause", () => setStatus("PAUSED"))} className="gap-2">
          {icon("pause", <PauseCircle />)}
          {t`Pause`}
        </Button>
      )}
      {session.status === "PAUSED" && (
        <Button type="button" variant="brand-outline" disabled={busy} onClick={() => run("resume", () => setStatus("COOKING"))} className="gap-2">
          {icon("resume", <PlayCircle />)}
          {t`Resume`}
        </Button>
      )}
      {showNext && (
        <Button type="button" variant="brand-outline" disabled={busy} onClick={() => run("next", () => nextStage.mutateAsync(session.id))} className="gap-2">
          {icon("next", <ChevronsRight />)}
          {t`Next stage`}
        </Button>
      )}
      {session.status !== "ON_DECK" && (
        <Button
          type="button"
          variant="success"
          size="icon"
          disabled={busy}
          aria-label={t`Mark done`}
          title={t`Mark done`}
          onClick={() => run("done", () => setStatus("COMPLETED"))}
        >
          {icon("done", <CheckCircle />)}
        </Button>
      )}
    </div>
  );
}
